import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, output, OutputEmitterRef, Signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { IconComponent } from '@libs/components/icons';
import { NavItemModel } from '@libs/shared/models';
import { NavigationTrackingService } from '@libs/shared/tracking';
import { ToggleNavigationItemComponent } from './toggle-navigation-item';

@Component({
  selector: 'ui-toggle-navigation',
  templateUrl: './toggle-navigation.component.html',
  styleUrls: ['./toggle-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    NgClass,
    ToggleNavigationItemComponent,
    IconComponent,
  ],
})
export class ToggleNavigationComponent {
  public topLevelTiles: InputSignal<boolean> = input(true);
  public navigationRoot: InputSignal<NavItemModel> = input.required();
  public selectedItem: InputSignal<NavItemModel | undefined> = input();
  public enableNavigationTracking: InputSignal<boolean> = input(true);

  public selectionChange: OutputEmitterRef<NavItemModel> = output();
  public navigationChange: OutputEmitterRef<NavItemModel> = output();

  public currentItem: Signal<NavItemModel> = computed(() => {
    return this.selectedItem() ?? this.navigationRoot();
  });

  public isRootCategory: Signal<boolean> = computed(() => {
    return this.currentItem() === this.navigationRoot();
  });

  public showPevButton: Signal<boolean> = computed(() => {
    return !this.isRootCategory();
  });

  public showBackToMainButton: Signal<boolean> = computed(() => {
    return !this.isRootCategory() && this.topLevelTiles();
  });

  public currentCategoryClass: Signal<string> = computed(() => {
    return this.isRootCategory() && this.topLevelTiles()
      ? 'category-grid'
      : 'category-list';
  });

  public navigationItemClass: Signal<string> = computed(() => {
    return `${this.currentCategoryClass()}-item`;
  });

  public currentCategoryAllItemsLinkTitle: Signal<string> = computed(() => {
    return this.currentItem().type === 'category'
      ? 'Alle Artikel'
      : `Alle ${this.currentItem().label}`;
  });

  public showItemChevron: Signal<boolean> = computed(() => {
    return !this.isRootCategory() || !this.topLevelTiles();
  });

  private readonly navigationTracker: NavigationTrackingService = inject(NavigationTrackingService);

  public selectNavItem(item: NavItemModel, mode?: 'link' | 'button'): void {
    if (item !== this.currentItem()) {
      this.selectionChange.emit(item);

      if (this.enableNavigationTracking()) {
        this.navigationTracker.trackItem(item);
      }
    }

    if (mode === 'link') {
      this.navigationChange.emit(item);
    }
  }

  public backToPreviousCategory(): void {
    const parent: NavItemModel | undefined = this.currentItem().parent;

    if (parent) {
      this.selectNavItem(parent);
    }
  }

  public backToMainCategory(): void {
    this.selectNavItem(this.navigationRoot());
  }
}
